import {
    saveEncodedValueToLocalStorage,
    getDecodedValueFromLocalStorage,
} from 'root/utils/dom/localStorage';
import { CookieMonster } from 'root/utils/cookies/cookieMonster';
import { GoogleTagManager } from 'root/utils/tracking/GA4/googleTagManager';
import { FormGTMObjectConstructor } from 'root/utils/tracking/GA4/formTracking';

interface Props {
    form: HTMLFormElement;
    errorClass?: string;
    target?: HTMLSelectElement;
}

function handleDomainPrefixField({ form, errorClass }: Props) {
    const domainPrefixField = form?.domain_prefix;
    const domainPrefixFieldParent = domainPrefixField?.parentElement;

    function hideDomainPrefix() {
        if (domainPrefixFieldParent && domainPrefixField) {
            domainPrefixFieldParent.style.display = 'none';
            domainPrefixField.required = false;
            domainPrefixFieldParent.dataset.fieldIsHidden = 'true';
        }
    }

    function showDomainPrefix() {
        if (domainPrefixFieldParent) {
            domainPrefixFieldParent.dataset.fieldIsHidden = 'false';
            domainPrefixFieldParent.style.display = 'block';
        }

        domainPrefixField && (domainPrefixField.required = true);
    }

    function toggleDisplay() {
        if (form.form_type?.value?.includes('free-trial')) {
            showDomainPrefix();
        } else {
            hideDomainPrefix();
        }
    }
    function removeOnSubmit() {
        if (
            domainPrefixFieldParent?.style.display === 'none' ||
            domainPrefixFieldParent?.dataset.fieldIsHidden === 'true'
        ) {
            domainPrefixFieldParent?.remove();
        }
    }

    return {
        toggleDisplay,
        removeOnSubmit,
    };
}

function toggleSMSOptinAndFootnoteDisplayOnProductSelection(
    form: HTMLFormElement,
    key: string,
    productValue: string
) {
    if (key === 'product') saveEncodedValueToLocalStorage('product', productValue);
    const productFromLocalStorage = getDecodedValueFromLocalStorage('product');
    const SMSoptin = form.querySelector('[data-form-extras="sms_opt_in"]') as HTMLElement;
    const SMSOptinFootnote = form.querySelector(
        '[data-form-extras="sms_opt_in_footnote"]'
    ) as HTMLElement;
    if (productFromLocalStorage?.toLowerCase() === 'golf') {
        SMSoptin && (SMSoptin.style.display = 'none');
        SMSOptinFootnote && (SMSOptinFootnote.style.display = 'none');
    } else {
        SMSoptin && (SMSoptin.style.display = 'block');
        SMSOptinFootnote && (SMSOptinFootnote.style.display = 'block');
    }
}

function createCodeFieldObserver(codeField: HTMLElement, form: HTMLFormElement) {
    return new IntersectionObserver(
        (entries, intersectionObserver) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    // Only fire GA4 event if the field is visible
                    const getVisibility = (field: HTMLElement) => {
                        return (
                            window.getComputedStyle(field).display !== 'none' &&
                            window.getComputedStyle(field).visibility !== 'hidden'
                        );
                    };
                    const handleVisibleCodeField = () => {
                        const formGTMObjectConstructor = new FormGTMObjectConstructor(
                            form
                        );
                        GoogleTagManager.sendEvent(
                            formGTMObjectConstructor.getFormGTMObject(
                                'field_displayed',
                                undefined,
                                'zip'
                            )
                        );
                        intersectionObserver.unobserve(entry.target);
                    };
                    if (getVisibility(codeField)) {
                        handleVisibleCodeField();
                    } else {
                        new MutationObserver((mutationList, mutationObserver) => {
                            if (mutationList.some(() => getVisibility(codeField))) {
                                handleVisibleCodeField();
                                mutationObserver.disconnect();
                            }
                        }).observe(form, {
                            childList: true,
                            subtree: true,
                            attributes: true,
                            attributeFilter: ['style', 'class'],
                        });
                    }
                }
            });
        },
        {
            root: null,
            threshold: 0.1,
        }
    );
}

function togglePostalCodeField(form: HTMLFormElement, show: boolean) {
    const postalCodeField = form
        .querySelector('[name=postal_code]')
        ?.closest<HTMLElement>('.gb-form__field');
    if (postalCodeField) {
        const postalCodeFieldObserver = createCodeFieldObserver(postalCodeField, form);
        if (show) {
            postalCodeField.classList.remove('gb-hide');
            postalCodeField.querySelector('input')?.setAttribute('required', 'true');
            // add event listener to send GA4 event when user sees the field
            if (postalCodeField.dataset.observing !== 'true') {
                postalCodeFieldObserver.observe(postalCodeField);
                postalCodeField.dataset.observing = 'true';
            }
        } else {
            postalCodeField.classList.add('gb-hide');
            postalCodeField.querySelector('input')?.removeAttribute('required');
            postalCodeFieldObserver.unobserve(postalCodeField);
            postalCodeField.dataset.observing = 'false';
        }
    }
}

function handleZipCodeField(form: HTMLFormElement, selectedProduct?: string) {
    const cm = new CookieMonster();
    const isPostalCodeEnabled = document.body.dataset.postalCode === 'enabled';
    const userCountryCode =
        cm.getCookie('geoConfig')?.country_code?.toLowerCase() ?? 'us';
    const userStateCode = cm.getCookie('geoConfig')?.state_code?.toLowerCase() ?? '';
    const enabledRegions = [{ countryCode: 'ca', stateCode: 'qc' }];
    const userIsInEnabledRegion = enabledRegions.some(
        region =>
            region.countryCode === userCountryCode && region.stateCode === userStateCode
    );
    const isAgnosticFormButWrongProduct = Boolean(
        form.dataset.formName?.includes('other') &&
            (selectedProduct === undefined ||
                (selectedProduct !== 'restaurant' && selectedProduct !== 'POSIOS'))
    );

    if (isPostalCodeEnabled && userIsInEnabledRegion && !isAgnosticFormButWrongProduct) {
        togglePostalCodeField(form, true);
    } else {
        togglePostalCodeField(form, false);
    }
}

function hideGolfFormOptionOnPCPages(form: HTMLFormElement) {
    if (
        window.location.pathname.includes('/pc/') &&
        document.body.dataset.formHideGolfPcPages === 'enabled'
    ) {
        const golfButton = form.querySelector(
            '[data-wizard-form-store-field="product|Golf"]'
        );
        golfButton?.classList.add('tw:hidden');
    }
}

export {
    handleDomainPrefixField,
    toggleSMSOptinAndFootnoteDisplayOnProductSelection,
    handleZipCodeField,
    hideGolfFormOptionOnPCPages,
};
